import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { useParams } from "react-router-dom";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import BadgeRow from "./components/BadgeRow";
import { BadgeListItem, BadgePickerProps, BadgeSelection } from "./types";

const PAGE_SIZE = 10;

const toSelection = (item: BadgeListItem): BadgeSelection => ({
  id: item.id,
  label: item.name,
});

const BadgePickerCst: React.FC<BadgePickerProps> = ({
  open,
  onClose,
  title,
  initialSelection,
  onConfirm,
}) => {
  const { t } = useTranslation("badge-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [selected, setSelected] = useState<BadgeSelection | null>(
    initialSelection,
  );

  const url = `${ServicesURL.getBadges}${
    corporateId ? `?corporateId=${corporateId}` : ""
  }`;

  const { items, totalItems, totalPages, isLoading } =
    usePaginatedList<BadgeListItem>(url, {
      page,
      pageSize,
      search: searchSubmitted || undefined,
    });

  useEffect(() => {
    if (!open) return;
    setSelected(initialSelection);
    setPage(1);
    setSearchSubmitted("");
  }, [open, initialSelection]);

  const selectedSet = useMemo(
    () => new Set<string | number>(selected ? [selected.id] : []),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (!next) {
      setSelected(null);
      return;
    }
    const numericId = typeof id === "string" ? Number(id) : id;
    const found = items.find((it) => it.id === numericId);
    if (found) setSelected(toSelection(found));
  };

  const handleSearch = (value: string) => {
    setSearchSubmitted(value);
    setPage(1);
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px 16px" }}>
        <SearchableMultiSelectListCst<BadgeListItem>
          selectionMode="single"
          showSelectedCount={false}
          items={items}
          getId={(it) => it.id}
          renderRow={({ item }) => <BadgeRow item={item} />}
          selectedIds={selectedSet}
          onToggle={handleToggle}
          initialSearchValue={searchSubmitted}
          onSearchSubmit={handleSearch}
          searchPlaceholder={t("search-placeholder") || ""}
          searchButtonLabel={t("search-button") || ""}
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          isLoading={isLoading}
          selectedCountLabel=""
          emptyLabel={t("empty") || ""}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <ButtonPrimary size="md" onClick={handleConfirm} disabled={!selected}>
          {t("confirm")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default BadgePickerCst;
